/**
 * Zoom-safe viewport height. Use this ANYWHERE a `100vh`-based height would
 * otherwise be written in `apps/platform`.
 *
 * The app-wide text zoom (`hooks/useTextZoom`) sets `body { zoom: z }`. `vh`
 * units resolve against the real viewport and are THEN scaled by that factor,
 * so a plain `height: 100vh` paints z×100vh and pushes a scrollbar onto the
 * whole document at any zoom above 1. Dividing the `vh` term by the factor —
 * read back off the `--text-zoom` custom property that `useTextZoom` mirrors
 * onto <html>, outside the zoomed subtree — cancels that exactly.
 *
 * The px offset is deliberately NOT divided. It stands for real chrome (a
 * header, a toolbar, the 24px page gutter) which is itself inside the zoomed
 * subtree and so already grows with everything else. Dividing it too would
 * reserve too little space at large scales.
 *
 *   vhMinus()    -> calc(100vh / var(--text-zoom, 1))
 *   vhMinus(176) -> calc(100vh / var(--text-zoom, 1) - 176px)
 *
 * The `1` fallback makes the expression exactly `100vh` at Default, so this is
 * free at the zoom almost everyone runs.
 *
 * Lives in `utils/` rather than beside the hook so the ~20 layout files that
 * need it aren't importing from `hooks/` for a pure CSS string.
 */
export const vhMinus = (offsetPx = 0) =>
  offsetPx === 0
    ? 'calc(100vh / var(--text-zoom, 1))'
    : `calc(100vh / var(--text-zoom, 1) - ${offsetPx}px)`

/**
 * The CSS zoom `el` actually renders at — the product of every `zoom` on it and its
 * ancestors. Use it to convert a PAINTED measurement into the element's own LAYOUT px
 * before writing it back into a style, or before mixing it with a layout-space value.
 *
 * WHY THIS EXISTS. Under the app-wide text zoom (`hooks/useTextZoom`, `zoom` on `#app`)
 * the DOM reports two coordinate spaces, and they do not agree:
 *
 *   PAINTED (already × z):  getBoundingClientRect, getClientRects, event clientX/clientY,
 *                           window.innerWidth/innerHeight
 *   LAYOUT  (unscaled):     offsetWidth/Height/Top, clientWidth/Height, scrollTop,
 *                           scrollHeight, scrollTo(), getComputedStyle, and every CSS
 *                           length you WRITE inside the zoomed subtree
 *
 * Measured in Chrome 148 at `zoom: 0.8`: a 200px-wide box reports offsetWidth 200 and
 * rect width 160; with scrollTop 100, a child at offsetTop 1010 sits 720px below its
 * scroller's rect top, i.e. (1010 − 100 − 10) × 0.8. So a painted value written back as
 * `style.width` gets multiplied by z a second time, and `scrollTop + rectDelta` lands
 * short by rectDelta × (1 − z). Divide painted values by this first.
 *
 * Prefers the native `currentCSSZoom` (Chrome/Edge 128+, Firefox 126+). Where that is
 * missing it falls back to painted ÷ layout width, which is the same number for an
 * untransformed element; 1 when the element has no width to compare.
 *
 * Per ELEMENT rather than a read of `--text-zoom`, because not everything sits under
 * `#app`: portalled surfaces (popover content, temporary drawers, dialogs) carry their
 * own re-zoom from `packages/ui/theme/theme.ts`, and body-level portals carry none.
 */
export const zoomOf = (el: Element | null | undefined): number => {
  if (!el) return 1
  const native = (el as Element & { currentCSSZoom?: number }).currentCSSZoom
  if (typeof native === 'number' && native > 0) return native
  if (el instanceof HTMLElement && el.offsetWidth > 0) {
    const painted = el.getBoundingClientRect().width
    if (painted > 0) return painted / el.offsetWidth
  }
  return 1
}
